import React, { useState, useEffect, useMemo, useRef } from 'react';
import { pbEngineer } from '../lib/pb';
import EmptyState from '../shared/components/EmptyState';
import Skeleton from '../shared/components/Skeleton';
import { Icons } from '../shared/icons';
import SafeIcon from '../common/SafeIcon';

// Engineer ↔ office/client messaging (spec §2.7). Threads grouped by client
// against the shared `messages` collection. sender is forced server-side by
// messages.pb.js (engineer role) and the office notify webhook fires for
// engineer sends too (migration 016 + hook extension).
export default function EngineerMessages() {
  const [loading, setLoading] = useState(true);
  const [messages, setMessages] = useState([]);
  const [activeClientId, setActiveClientId] = useState(null);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const bubblesRef = useRef(null);

  const engineer = pbEngineer.authStore.record;

  useEffect(() => {
    let unsub = null;
    let active = true;

    const load = async () => {
      try {
        const records = await pbEngineer.collection('messages').getFullList({
          sort: 'created',
          expand: 'client_id',
        });
        if (!active) return;
        setMessages(records);
        const first = records[0];
        if (first) setActiveClientId((cur) => cur || first.client_id);
      } catch (err) {
        console.error('Failed to load messages:', err);
        if (active) setError('Could not load messages. Check your connection.');
      } finally {
        if (active) setLoading(false);
      }
    };

    load();

    // SDK 0.28: subscribe() is async → keep the resolved UnsubscribeFunc.
    pbEngineer.collection('messages').subscribe('*', (event) => {
      if (!active) return;
      if (event.action === 'create' || event.action === 'update') {
        const rec = event.record;
        setMessages((current) => {
          const exists = current.some((m) => m.id === rec.id);
          if (event.action === 'create' && exists) return current;
          if (exists) return current.map((m) => (m.id === rec.id ? rec : m));
          return [...current, rec];
        });
      }
    })
      .then((fn) => { unsub = fn; })
      .catch((err) => console.warn('[engineer] messages realtime failed', err?.message || err));

    return () => {
      active = false;
      if (unsub) Promise.resolve(unsub()).catch(() => {});
    };
  }, []);

  // Threads: group by client, most recent first.
  const threads = useMemo(() => {
    const byClient = {};
    for (const m of messages) {
      const clientId = m.client_id;
      if (!byClient[clientId]) byClient[clientId] = [];
      byClient[clientId].push(m);
    }
    return Object.entries(byClient)
      .map(([clientId, msgs]) => {
        const last = msgs[msgs.length - 1];
        return {
          clientId,
          name: last.expand?.client_id?.name || msgs[0].expand?.client_id?.name || 'Client',
          last: last.body,
          lastAt: last.created,
          count: msgs.length,
        };
      })
      .sort((a, b) => new Date(b.lastAt) - new Date(a.lastAt));
  }, [messages]);

  const activeMessages = useMemo(
    () => messages.filter((m) => m.client_id === activeClientId),
    [messages, activeClientId],
  );

  const activeThread = threads.find((t) => t.clientId === activeClientId);

  // Auto-scroll to newest bubble.
  useEffect(() => {
    if (bubblesRef.current) {
      bubblesRef.current.scrollTop = bubblesRef.current.scrollHeight;
    }
  }, [activeClientId, activeMessages.length]);

  const sendMessage = async () => {
    const body = draft.trim();
    if (!body || !activeClientId || sending) return;
    setSending(true);
    setError(null);
    try {
      await pbEngineer.collection('messages').create({
        client_id: activeClientId,
        body,
      });
      setDraft('');
    } catch (err) {
      console.error('Failed to send message:', err);
      setError('Message failed to send. Please try again.');
    } finally {
      setSending(false);
    }
  };

  const fmtTime = (iso) =>
    new Date(iso).toLocaleString(undefined, {
      day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit',
    });

  if (loading) return <div className="page-container"><Skeleton rows={6} /></div>;

  return (
    <div className="page-container">
      <div className="page-header">
        <h1>Messages</h1>
        <p>Chat with clients and the office</p>
      </div>

      {error && <p className="form-hint" style={{ color: '#b91c1c', marginBottom: 12 }}>{error}</p>}

      {threads.length === 0 ? (
        <EmptyState
          title="No conversations yet"
          message="Messages between you, clients and the office will appear here."
          icon={Icons.MessageCircle}
        />
      ) : (
        <div className="messages-layout">
          <div className="thread-list">
            {threads.map((thread) => (
              <button
                key={thread.clientId}
                className={`thread-item ${activeClientId === thread.clientId ? 'active' : ''}`}
                onClick={() => setActiveClientId(thread.clientId)}
              >
                <strong>{thread.name}</strong>
                <span>{thread.last}</span>
                <span className="thread-time">{fmtTime(thread.lastAt)}</span>
              </button>
            ))}
          </div>

          <div className="chat-panel">
            <div className="chat-head">
              <h2>{activeThread?.name || 'Conversation'}</h2>
              <p>{activeMessages.length} message(s)</p>
            </div>

            <div className="chat-bubbles" ref={bubblesRef}>
              {activeMessages.map((m) => {
                const mine = m.sender === 'engineer';
                return (
                  <div key={m.id} className={`bubble ${mine ? 'mine' : 'client'}`}>
                    {m.body}
                    <span className="bubble-meta">
                      {m.sender_name || (mine ? engineer?.name : 'Client')} · {fmtTime(m.created)}
                    </span>
                  </div>
                );
              })}
            </div>

            <div className="chat-send">
              <input
                type="text"
                placeholder="Type a message…"
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && sendMessage()}
                aria-label="Message body"
              />
              <button className="primary-button" onClick={sendMessage} disabled={sending || !draft.trim()}>
                <SafeIcon icon={Icons.Send} size={15} />
                Send
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
